import React, { useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaLock, FaArrowLeft, FaShieldAlt, FaUserShield } from "react-icons/fa";
import { motion } from "framer-motion";

const PermissionDenied = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Clear redirect flag when component mounts
  useEffect(() => {
    // Clear the redirecting flag after page loads
    const timer = setTimeout(() => {
      sessionStorage.removeItem("redirecting_403");
    }, 500);

    return () => {
      clearTimeout(timer);
      // Also clear on unmount (when user navigates away)
      sessionStorage.removeItem("redirecting_403");
    };
  }, []);

  const message = useMemo(() => {
    // Try to get message from URL query params
    const searchParams = new URLSearchParams(location.search);
    const urlMessage = searchParams.get("message");

    if (urlMessage) {
      return decodeURIComponent(urlMessage);
    }

    // Fallback to location state
    if (location.state?.message) {
      return location.state.message;
    }

    // Default message
    return "You do not have permission to access this resource. Please contact your administrator if you believe this is an error.";
  }, [location]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-br from-red-500 to-red-600 px-8 py-12 text-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute top-1/2 right-0 w-48 h-48 bg-white rounded-full translate-x-1/2"></div>
              <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-white rounded-full translate-y-1/2"></div>
            </div>

            {/* Error Code */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="relative"
            >
              <h1 className="text-9xl font-bold text-white opacity-20 mb-4">
                403
              </h1>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white rounded-full p-6 shadow-xl">
                  <FaLock className="w-16 h-16 text-red-500" />
                </div>
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="relative mt-8"
            >
              <h2 className="text-4xl font-bold text-white mb-2">
                Access Denied
              </h2>
              <p className="text-red-100 text-lg">
                You don't have permission to access this page
              </p>
            </motion.div>
          </div>

          {/* Content Section */}
          <div className="px-8 py-10">
            {/* Error Message */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 mb-8"
            >
              <div className="flex items-start">
                <FaShieldAlt className="w-6 h-6 text-red-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Permission Required
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{message}</p>
                </div>
              </div>
            </motion.div>

            {/* Information Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid md:grid-cols-2 gap-6 mb-8"
            >
              {/* What Happened Card */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 rounded-lg p-3 mr-3">
                    <FaUserShield className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    What Happened?
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span>
                      Your account doesn't have the required permissions
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span>This feature is restricted to certain roles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span>You may have been logged out or session expired</span>
                  </li>
                </ul>
              </div>

              {/* What Can You Do Card */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 rounded-lg p-3 mr-3">
                    <FaShieldAlt className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    What Can You Do?
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2 mt-1">•</span>
                    <span>Contact your administrator for access</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2 mt-1">•</span>
                    <span>Go back to the previous page</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2 mt-1">•</span>
                    <span>Return to the dashboard</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={handleGoBack}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
              >
                <FaArrowLeft className="w-5 h-5" />
                Go Back
              </button>
              <button
                onClick={handleGoHome}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
              >
                Go to Dashboard
              </button>
            </motion.div>

            {/* Help Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8 text-center"
            >
              <p className="text-sm text-gray-500">
                Need help?{" "}
                <a
                  href="mailto:support@tykoon.ai"
                  className="text-red-500 hover:text-red-600 font-medium underline"
                >
                  Contact Support
                </a>
              </p>
            </motion.div>
          </div>
        </div>

        {/* Footer Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-6 text-center"
        >
          <p className="text-sm text-gray-500">
            Error Code: <span className="font-mono font-semibold">403</span> -
            Forbidden Access
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PermissionDenied;
